import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Block, Inline, INLINES } from '@contentful/rich-text-types';
import { applyDictionary } from '@belong/type-utils';
import { IOrganismNbnPlanCard, IPlanCard } from '@belong/types';
import CONTENT from './content.json';

export const CURRENCY = 'AUD';
export const FREQUENCY = 'MONTH';
export const BRAND_NAME = 'Belong';

type IPlanObject = IPlanCard | IOrganismNbnPlanCard;

const isIOrganismNbnPlanCard = (plan: IPlanObject): plan is IOrganismNbnPlanCard => Object.hasOwn(plan, 'product');

const isIOrganismNbnPlanCardArray = (plans: IPlanObject[]): plans is IOrganismNbnPlanCard[] =>
  plans.length > 0 && plans.every(isIOrganismNbnPlanCard);

/**
 * Generates a plan description based on plan type
 * @param planObject - The plan object containing details like speed, inclusions, and plan type
 * @returns A string description of the plan
 */
export const getPlanDescription = (planObject: IPlanCard): string => {
  const { plan, typicalSpeedsCopy, inclusions, planType } = planObject;
  const stringPlanTypicalSpeedsCopy = (
    documentToPlainTextString(typicalSpeedsCopy?.html as Block | Inline) || ''
  ).trim();

  //  Build inclusions string
  const planInclusions = (inclusions ?? [])
    .filter(inclusion => inclusion.available !== false)
    .map(inclusion => inclusion.label)
    .join(', ');

  const { data: planData, price } = plan;
  const normalizedPlanType = planType.toLowerCase();

  return applyDictionary(
    {
      value: `${planData?.value ?? 0}${planData?.unit}`,
      typicalSpeedsCopy: stringPlanTypicalSpeedsCopy,
      inclusions: planInclusions,
      price: `${plan.price.unit}${price.value}`
    },
    CONTENT[normalizedPlanType]
  );
};

/**
 * Generates offer schema for a plan
 * @param planObject - The plan object containing pricing details
 * @param pageUrl - The URL of the page where the offer is listed
 * @returns An object representing the offer schema
 * @description This function constructs a structured data representation of the offer for a plan,
 *              including the price, currency, and any sale price if applicable.
 */
export const getOffers = (planObject: IPlanCard | IOrganismNbnPlanCard, pageUrl: string, siteUrl: string): any => {
  const price = isIOrganismNbnPlanCard(planObject) ? planObject.product.price : planObject.plan.price?.value;

  const RRPOffer = {
    '@type': 'Offer',
    url: pageUrl,
    price,
    priceCurrency: CURRENCY,
    availability: 'https://schema.org/InStock',
    eligibleRegion: { '@type': 'Country', name: 'Australia' },
    seller: { '@id': `${siteUrl}#org` },
    priceSpecification: [
      {
        '@type': 'UnitPriceSpecification',
        priceCurrency: CURRENCY,
        price,
        unitText: FREQUENCY,
        priceType: 'https://schema.org/SRP'
      }
    ]
  };

  if (isIOrganismNbnPlanCard(planObject)) {
    if (planObject.product.isOfferProduct) {
      Object.assign(RRPOffer, {
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          unitText: FREQUENCY,
          priceType: 'https://schema.org/StrikethroughPrice',
          price: planObject.product.basePrice,
          priceCurrency: CURRENCY
        }
      });
    }
  } else if (planObject.offer) {
    // mobile and data plans offer details
    const { description, endDate, name, rewards, startDate } = planObject.offer;
    Object.assign(RRPOffer, {
      addOn: {
        '@type': 'Offer',
        name,
        priceCurrency: CURRENCY,
        price: 0,
        eligibleDuration:
          rewards.length > 0 && rewards[0].productAttributes.durationInMonths
            ? `P${rewards[0].productAttributes.durationInMonths}M`
            : undefined,
        validFrom: startDate,
        validThrough: endDate,
        category: 'Promotion',
        itemOffered: {
          '@type': 'Service',
          name,
          description
        }
      }
    });
  }

  return RRPOffer;
};

/**
 * Filters and validates plans for schema markup
 */
export const getValidPlans = (plans: IPlanCard[]): IPlanCard[] =>
  plans.filter(planObject => planObject.contentType === 'organismPlanCard');

/**
 * Generates a plan description specifically for NBN plans
 * @param planObject - The plan object containing details like speed, inclusions, and plan type
 * @returns A string description of the plan
 */
export const getNbnPlanDescription = (planObject: IOrganismNbnPlanCard, sectionNbnPlanSummary: any): string => {
  const { product, suitabilityRows = [] } = planObject;

  const { eveningDownstreamSpeedMbps, eveningUpstreamSpeedMbps, speedUnit } = product;

  //  Build inclusions string
  const inclusions = sectionNbnPlanSummary.inclusions
    .map(({ title }) => documentToPlainTextString(title.html))
    .join(', ');

  // Build suitabilty string
  const suitability = suitabilityRows.map(({ title }) => documentToPlainTextString(title.html)).join(', ');

  return applyDictionary(
    {
      downloadSpeed: `${eveningDownstreamSpeedMbps}${speedUnit}`,
      uploadSpeed: `${eveningUpstreamSpeedMbps}${speedUnit}`,
      inclusions,
      suitability
    },
    CONTENT.nbn
  );
};

interface IGenerateProductSchema {
  pageUrl: string;
  plans: IPlanCard[] | IOrganismNbnPlanCard[];
  sectionNbnPlanSummary: any;
  siteUrl: string;
}
/**
 * Generates product schema from plans
 */
export const generateProductSchema = ({
  pageUrl,
  plans,
  sectionNbnPlanSummary,
  siteUrl
}: IGenerateProductSchema): any => {
  const baseSchema = {
    '@type': 'Product',
    brand: { '@type': 'Brand', name: BRAND_NAME }
  };

  if (isIOrganismNbnPlanCardArray(plans)) {
    return plans.map(planObject => ({
      ...baseSchema,
      name: planObject.product.displayLabel,
      description: getNbnPlanDescription(planObject, sectionNbnPlanSummary),
      offers: getOffers(planObject, pageUrl, siteUrl)
    }));
  }

  return (
    getValidPlans(plans)
      .map(planObject => ({
        ...baseSchema,
        name: planObject.title,
        description: getPlanDescription(planObject),
        offers: getOffers(planObject, pageUrl, siteUrl)
      }))
      // Filter out null values
      .filter((item): item is NonNullable<typeof item> => item !== null)
  );
};

/**
 * Converts FAQ answer to HTML format with embedded entries
 */
export const getFAQAnswer = (faq: any, siteUrl: string): string => {
  if (!faq.body || !faq.body.html) {
    return '';
  }

  const options = {
    renderNode: {
      [INLINES.EMBEDDED_ENTRY]: ({ data }): any => {
        const entry = data.target;
        const href = `${siteUrl}${entry.fields?.url?.fields?.url || '#'}`;
        const text = entry.fields?.label || 'Link';
        return `<a href="${href}">${text}</a>`;
      }
    }
  };
  return documentToHtmlString(faq.body.html, options);
};

/**
 * Generates FAQ schema
 */
export const generateFAQSchema = (faqs: any[], siteUrl: string): any => {
  if (faqs.length === 0) {
    return null;
  }

  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.label,
      acceptedAnswer: {
        '@type': 'Answer',
        text: getFAQAnswer(faq, siteUrl)
      }
    }))
  };
};
